import { useEffect, useState } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CURRENCY_SYMBOLS = { gbp: '£', usd: '$', eur: '€' };

const SOURCE_LABELS = {
  widget: 'Online booking',
  deposit_link: 'Staff deposit link',
  phone: 'Phone',
  admin: 'Admin',
};

function money(item) {
  return `${CURRENCY_SYMBOLS[item.deposit_currency] || '£'}${Number(item.deposit_amount || 0).toFixed(2)}`;
}

function when(item) {
  if (!item.booking_date) return '-';
  try {
    return `${format(new Date(`${item.booking_date}T00:00:00`), 'EEE d MMM yyyy')} · ${item.booking_time || ''}`;
  } catch {
    return `${item.booking_date} ${item.booking_time || ''}`;
  }
}

function created(item) {
  if (!item.created_at) return '-';
  try {
    return format(new Date(item.created_at), 'd MMM yyyy, HH:mm');
  } catch {
    return item.created_at;
  }
}

function Section({ title, hint, items, tone }) {
  if (!items?.length) return null;
  const total = items.reduce((sum, i) => sum + Number(i.deposit_amount || 0), 0);
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className={`font-semibold ${tone === 'urgent' ? 'text-red-700' : 'text-slate-800'}`}>
          {title} ({items.length}) · £{total.toFixed(2)}
        </h3>
        {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      </div>
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2">Booking</th>
              <th className="px-3 py-2">Customer</th>
              <th className="px-3 py-2">Deposit</th>
              <th className="px-3 py-2">Source</th>
              <th className="px-3 py-2">Created</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className="border-t align-top">
                <td className="px-3 py-2 whitespace-nowrap">
                  <p className="font-medium">{when(item)}</p>
                  <p className="text-xs text-muted-foreground">
                    {item.party_size} guests · {item.status?.replace('_', ' ')}
                  </p>
                </td>
                <td className="px-3 py-2">
                  <p className="font-medium">{item.customer_name || '-'}</p>
                  {item.customer_phone && <p className="text-xs text-muted-foreground">{item.customer_phone}</p>}
                  {item.customer_email && <p className="text-xs text-muted-foreground break-all">{item.customer_email}</p>}
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <Badge className="bg-red-600 text-white hover:bg-red-600">Not paid {money(item)}</Badge>
                </td>
                <td className="px-3 py-2 text-xs">
                  {SOURCE_LABELS[item.source] || item.source || '-'}
                  {item.created_by_user_name && <p className="text-muted-foreground">by {item.created_by_user_name}</p>}
                </td>
                <td className="px-3 py-2 text-xs whitespace-nowrap">{created(item)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Platform admin: drill into a venue's "Pending deposits" figure. */
export default function UnpaidDepositsDialog({ restaurant, onOpenChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!restaurant?.id) return;
    setData(null);
    setError(null);
    setLoading(true);
    axios
      .get(`${API}/platform/restaurants/${restaurant.id}/unpaid-deposits`)
      .then((res) => setData(res.data))
      .catch((err) => setError(err.response?.data?.detail || 'Could not load unpaid deposits'))
      .finally(() => setLoading(false));
  }, [restaurant?.id]);

  const empty = data && !data.upcoming.length && !data.past.length && !data.closed.length;

  return (
    <Dialog open={Boolean(restaurant)} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Unpaid deposits · {restaurant?.name}</DialogTitle>
          <DialogDescription>
            Bookings where a deposit was requested but never paid. No money has been taken for these.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        )}
        {error && <p className="text-sm text-red-700">{error}</p>}
        {empty && <p className="text-sm text-muted-foreground py-6 text-center">No unpaid deposits.</p>}

        {data && !empty && (
          <div className="space-y-6">
            {data.upcoming.length > 0 && (
              <div className="flex items-start gap-2 rounded-lg border-2 border-red-300 bg-red-50 p-3 text-sm text-red-800">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <p>
                  Upcoming bookings without a paid deposit are shown in red on the venue's tablet and Bookings page
                  ("Deposit not paid" filter). Staff can send a payment link, mark it paid if taken by phone, or cancel.
                </p>
              </div>
            )}
            <Section title="Upcoming" hint="Guests may turn up. Deposit NOT paid" items={data.upcoming} tone="urgent" />
            <Section title="Past" hint="Booking time has passed" items={data.past} />
            <Section title="Cancelled / no-show" items={data.closed} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
